import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { StructureSummaryBarProps } from './StructureSummaryBar.props';
import { COLORS } from '../../../configs/colors';
import { v4AccentButtonSx, v4GhostButtonSx } from '../../atoms/Button';
import { TablePill } from '../../atoms/TablePill';
import { TagChips } from '../TagChips';

/**
 * Separates two metadata groups sitting on the same line. Hidden on phones,
 * where every group takes a line of its own and the rule would just hang off
 * the left edge separating nothing.
 */
const Divider: React.FC = () => (
  <Box
    sx={{
      width: '1px',
      height: 22,
      bgcolor: 'v4.border',
      flexShrink: 0,
      display: { xs: 'none', sm: 'block' },
    }}
  />
);

// Matches the Part Type detail page's pill exactly — the two headers sit one
// nav click apart and an unsaved warning that changes shape between them reads
// as two different warnings. #78350f on #fef3c7 is 8.15:1.
const unsavedSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: COLORS.TagAmberText,
  bgcolor: COLORS.TagAmberBg,
  borderRadius: '4px',
  px: 1,
  py: '3px',
  whiteSpace: 'nowrap' as const,
};

const labelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,
};

/**
 * The sticky identity bar on the structure detail page — the structure's name,
 * what kind of structure it is, where it sits in the hierarchy, and the Save
 * that commits the whole form.
 *
 * It is the structure counterpart to `SummaryBar` (jobs) and `PartSummaryBar`
 * (parts), and deliberately keeps their two-row shape: identity on top,
 * labelled context underneath, actions pinned right.
 */
export const StructureSummaryBar: React.FC<StructureSummaryBarProps> = ({
  name,
  structureTypeName,
  structureId,
  parentHierarchy,
  tags = [],
  isActive,
  hideStatus = false,
  isUnzoned = false,
  zone,
  regionType,
  saving = false,
  hasUnsavedChanges = false,
  canEdit = true,
  saveDisabled = false,
  saveLabel = 'Save',
  onSave,
  onCancel,
  rightSlot,
}) => {
  const hasParent =
    parentHierarchy !== undefined && parentHierarchy !== null && parentHierarchy !== '';

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'v4.border',
        borderRadius: '8px',
        px: 2,
        py: 1,
        boxSizing: 'border-box',
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      {/* `1 1 260px` rather than `flex: 1`: below that the actions drop to their
          own line instead of being squeezed alongside a truncated name. */}
      <Box
        sx={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {/* Row 1 — what this is: name, type, whether it is in use */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <Tooltip title="Structure location" placement="top" arrow>
            <AccountTreeOutlinedIcon sx={{ fontSize: 22, color: 'v4.accent', flexShrink: 0 }} />
          </Tooltip>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              minWidth: 0,
              // Wrap to a second line before truncating: the name is the
              // subject of the page, and a sticky bar can afford one extra
              // line more readily than it can afford an unreadable name.
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              color: name ? 'text.primary' : 'text.secondary',
            }}
            title={name}
          >
            {name || 'Untitled structure'}
          </Typography>

          {/* A plain category label, so `neutral` — see TablePillTone. It reads
              as a pill rather than as running text because everything else on
              this line (Active, Unzoned, and the zone and region below) is one,
              and a bare ": Outlet" was the odd one out. */}
          {structureTypeName && (
            <TablePill tone="neutral" size="sm" ariaLabel={`Structure type ${structureTypeName}`}>
              {structureTypeName}
            </TablePill>
          )}

          {!hideStatus && isActive !== undefined && (
            <TablePill tone={isActive ? 'positive' : 'neutral'} size="sm">
              {isActive ? 'Active' : 'Inactive'}
            </TablePill>
          )}

          {isUnzoned && (
            <Tooltip title="No zone matched from this structure's address" placement="top" arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <TablePill tone="warning" size="sm">
                  Unzoned
                </TablePill>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Row 2 — where it sits, and how it is labelled. Each group carries its
            own leading divider so a divider never wraps onto a line alone. */}
        {(hasParent || structureId || zone || regionType || tags.length > 0) && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}
          >
            {structureId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={labelSx}>ID</Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{structureId}</Typography>
              </Box>
            )}

            {hasParent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {structureId && <Divider />}
                <Typography sx={labelSx}>Parent</Typography>
                <Box
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {parentHierarchy}
                </Box>
              </Box>
            )}

            {zone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {(hasParent || structureId) && <Divider />}
                <Typography sx={labelSx}>Zone</Typography>
                <TablePill tone="info">{zone}</TablePill>
              </Box>
            )}

            {regionType && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {(hasParent || structureId || zone) && <Divider />}
                <Typography sx={labelSx}>Region</Typography>
                <TablePill tone="neutral">{regionType}</TablePill>
              </Box>
            )}

            {tags.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {(hasParent || structureId || zone || regionType) && <Divider />}
                <TagChips tags={tags} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, ml: 'auto', alignItems: 'center' }}>
        {canEdit && hasUnsavedChanges && !saving && (
          <Typography sx={unsavedSx} role="status">
            Unsaved changes
          </Typography>
        )}
        {rightSlot}
        {canEdit && onCancel && (
          <Button variant="outlined" size="small" onClick={onCancel} sx={v4GhostButtonSx}>
            Cancel
          </Button>
        )}
        {canEdit && onSave && (
          <Button
            variant="contained"
            size="small"
            onClick={onSave}
            disabled={saveDisabled || saving}
            sx={v4AccentButtonSx}
            startIcon={
              saving ? <CircularProgress size={12} sx={{ color: 'common.white' }} /> : undefined
            }
          >
            {saving ? 'Saving…' : saveLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};
